import React from "react";
import { Text, Link } from "@chakra-ui/react";

export default function FormattedText({ text, color = "gray.800" }) {
  if (!text) return null;

  // Parse markdown-like formatting
  const parseText = (message) => {
    let result = [];
    let remaining = message;
    
    // Process links first (they have the most complex pattern)
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/;
    
    while (remaining.length > 0) {
      const linkMatch = linkRegex.exec(remaining);
      
      if (linkMatch) {
        const beforeLink = remaining.substring(0, linkMatch.index);
        
        // Process text before the link for other formatting
        if (beforeLink) {
          result.push(...processFormattedText(beforeLink));
        }
        
        // Add the link
        result.push(
          <Link
            key={`link-${result.length}`}
            href={linkMatch[2]}
            isExternal
            color="gray.700"
            textDecoration="underline"
            _hover={{ color: "gray.900" }}
            display="inline"
          >
            {linkMatch[1]}
          </Link>
        );
        
        // Continue with text after the link
        remaining = remaining.substring(linkMatch.index + linkMatch[0].length);
      } else {
        // No more links, process the remaining text
        result.push(...processFormattedText(remaining));
        break;
      }
    }
    
    return result;
  };
  
  // Process other formatting (bold, italic, underline, strikethrough)
  const processFormattedText = (text) => {
    const elements = [];
    
    // Apply formatting patterns
    const formattingPatterns = [
      { pattern: /\*([^*]+)\*/g, tag: 'strong' },
      { pattern: /__([^_]+)__/g, tag: 'u' },
      { pattern: /_([^_]+)_/g, tag: 'em' },
      { pattern: /~([^~]+)~/g, tag: 's' },
    ];
    
    // Create combined regex for splitting
    const combinedRegex = /(\*[^*]+\*|__[^_]+__|_[^_]+_|~[^~]+~)/g;
    const parts = text.split(combinedRegex);
    
    parts.forEach((part, index) => {
      if (!part) return;
      
      let matched = false;
      
      for (const { pattern, tag } of formattingPatterns) {
        const match = pattern.exec(part);
        pattern.lastIndex = 0; // Reset regex
        
        if (match) {
          matched = true;
          const Component = tag;
          elements.push(
            <Component key={`format-${index}`}>
              {match[1]}
            </Component>
          );
          break;
        }
      }
      
      if (!matched) {
        elements.push(
          <React.Fragment key={`text-${index}`}>
            {part}
          </React.Fragment>
        );
      }
    });
    
    return elements;
  };

  return (
    <Text as="span" color={color}>
      {parseText(text)}
    </Text>
  );
}